import type { WhatsAppTemplateKey } from './constants/index.js';
import { DEFAULT_WHATSAPP_TEMPLATES } from './constants/index.js';

/**
 * The single WhatsApp URL builder. Nothing else in the codebase concatenates a
 * wa.me link, which is what keeps 32 hand-written variants from reappearing.
 */
export function buildWhatsAppUrl({ phone, message }: { phone: string; message?: string }): string {
  const digits = String(phone ?? '').replace(/[^\d]/g, '');
  if (!digits) return 'https://wa.me/';
  const base = `https://wa.me/${digits}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}

/** Fill {placeholders} in a template; unknown keys are left untouched. */
export function renderTemplate(template: string, vars: Record<string, string | number | null | undefined>): string {
  return String(template ?? '').replace(/\{(\w+)\}/g, (match, key: string) => {
    const v = vars[key];
    return v === undefined || v === null || v === '' ? match : String(v);
  });
}

export function whatsappMessage(
  key: WhatsAppTemplateKey,
  vars: Record<string, string | number | null | undefined> = {},
  overrides?: Partial<Record<WhatsAppTemplateKey, string>>,
): string {
  const template = overrides?.[key] || DEFAULT_WHATSAPP_TEMPLATES[key];
  return renderTemplate(template, vars);
}

/** `tel:` href from any stored number, always E.164 with a leading plus. */
export function telHref(phone: string): string {
  const digits = String(phone ?? '').replace(/[^\d]/g, '');
  if (!digits) return 'tel:';
  const e164 = digits.length === 10 ? `91${digits}` : digits.replace(/^0+/, '');
  return `tel:+${e164}`;
}

/** "919008718333" -> "+91 90087 18333" */
export function formatPhone(phone: string): string {
  const digits = String(phone ?? '').replace(/[^\d]/g, '');
  const national = digits.length > 10 ? digits.slice(-10) : digits;
  const cc = digits.length > 10 ? digits.slice(0, digits.length - 10) : '91';
  if (national.length !== 10) return phone;
  return `+${cc} ${national.slice(0, 5)} ${national.slice(5)}`;
}

/** "919008718333" -> "90087 18333" — the form the header button uses. */
export function formatPhoneNational(phone: string): string {
  const digits = String(phone ?? '').replace(/[^\d]/g, '');
  const national = digits.length > 10 ? digits.slice(-10) : digits;
  if (national.length !== 10) return phone;
  return `${national.slice(0, 5)} ${national.slice(5)}`;
}

export function formatRupees(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '';
  return `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(amount)}`;
}

export function slugify(input: string): string {
  return String(input ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/&/g, ' and ')
    .replace(/₹/g, ' rs ')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/[\s-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120) || 'item';
}

/** Normalise any site path to a leading and trailing slash, matching legacy URLs. */
export function normalisePath(input: string): string {
  let p = String(input ?? '/').split('?')[0]!.split('#')[0]!;
  if (!p.startsWith('/')) p = `/${p}`;
  if (p !== '/' && !p.endsWith('/')) p = `${p}/`;
  return p.replace(/\/{2,}/g, '/');
}

const MINUTES = (hhmm: string): number => {
  const [h, m] = String(hhmm ?? '').split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

export function formatTime12h(hhmm: string): string {
  const [hRaw, mRaw] = String(hhmm ?? '').split(':');
  const h = Number(hRaw);
  const m = Number(mRaw);
  if (Number.isNaN(h)) return hhmm;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, '0')} ${suffix}` : `${h12}:00 ${suffix}`;
}

export interface OpenState {
  open: boolean;
  label: string;
  todayHours: string;
}

/**
 * Whether a branch is open right now, evaluated in the salon's timezone rather
 * than the visitor's, plus the human label the UI shows.
 */
export function branchOpenState(
  openingHours: { day: string; closed: boolean; opens: string; closes: string }[],
  holidayHours: { date: string; closed: boolean; opens?: string; closes?: string; label?: string }[] = [],
  now: Date = new Date(),
  timeZone = 'Asia/Kolkata',
): OpenState {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false, weekday: 'short',
  });
  const parts = Object.fromEntries(fmt.formatToParts(now).map((p) => [p.type, p.value]));
  const dateStr = `${parts.year}-${parts.month}-${parts.day}`;
  const weekday = String(parts.weekday ?? '').toLowerCase().slice(0, 3);
  const nowMin = Number(parts.hour) * 60 + Number(parts.minute);

  const holiday = holidayHours.find((h) => h.date === dateStr);
  const today = holiday
    ? { closed: holiday.closed, opens: holiday.opens ?? '', closes: holiday.closes ?? '' }
    : openingHours.find((h) => h.day === weekday);

  if (!today || today.closed || !today.opens || !today.closes) {
    return { open: false, label: holiday?.label || 'Closed today', todayHours: 'Closed' };
  }

  const openMin = MINUTES(today.opens);
  const closeMin = MINUTES(today.closes);
  // A closing time before the opening time means the branch runs past midnight.
  const open = closeMin > openMin
    ? nowMin >= openMin && nowMin < closeMin
    : nowMin >= openMin || nowMin < closeMin;

  const todayHours = `${formatTime12h(today.opens)}–${formatTime12h(today.closes)}`;
  return {
    open,
    label: open ? `Open today · ${todayHours}` : `Closed · Opens ${formatTime12h(today.opens)}`,
    todayHours,
  };
}

/** Group consecutive days with identical hours: "Mon – Sun · 8:00 AM–9:30 PM". */
export function summariseHours(
  openingHours: { day: string; closed: boolean; opens: string; closes: string }[],
): string[] {
  const ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const LABEL: Record<string, string> = {
    mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
  };
  const sorted = ORDER
    .map((d) => openingHours.find((h) => h.day === d))
    .filter(Boolean) as typeof openingHours;
  if (!sorted.length) return [];

  const out: string[] = [];
  let runStart = 0;
  const key = (h: (typeof sorted)[number]) => (h.closed ? 'closed' : `${h.opens}-${h.closes}`);

  for (let i = 1; i <= sorted.length; i += 1) {
    if (i === sorted.length || key(sorted[i]!) !== key(sorted[runStart]!)) {
      const a = sorted[runStart]!;
      const b = sorted[i - 1]!;
      const days = runStart === i - 1
        ? LABEL[a.day]
        : `${LABEL[a.day]} – ${LABEL[b.day]}`;
      const hours = a.closed ? 'Closed' : `${formatTime12h(a.opens)}–${formatTime12h(a.closes)}`;
      out.push(`${days}  ·  ${hours}`);
      runStart = i;
    }
  }
  return out;
}

/** Google Maps directions URL built from an address when no share link is set. */
export function directionsUrl(input: {
  mapsUrl?: string;
  directionsUrl?: string;
  latitude?: number | null;
  longitude?: number | null;
  addressLines?: string[];
  name?: string;
}): string {
  if (input.directionsUrl) return input.directionsUrl;
  if (input.mapsUrl) return input.mapsUrl;
  if (input.latitude != null && input.longitude != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${input.latitude},${input.longitude}`;
  }
  const q = [input.name, ...(input.addressLines ?? [])].filter(Boolean).join(', ');
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}`;
}

/**
 * The embeddable map for a branch, built the way the original contact page
 * built it: a plain `maps.google.com` query on the address, which needs no API
 * key and no billing account.
 *
 * The address comes from the Branch record, so moving the salon updates the map
 * along with everything else.
 */
export function mapEmbedUrl(input: {
  addressLines?: string[];
  latitude?: number | null;
  longitude?: number | null;
  zoom?: number;
}): string {
  const zoom = input.zoom ?? 12;
  const q = input.latitude != null && input.longitude != null
    ? `${input.latitude},${input.longitude}`
    // The stored lines already end in commas, so they are stripped before
    // joining rather than producing "V V Ventures,, Vasanthapura".
    : (input.addressLines ?? [])
      .map((line) => line.replace(/[\s,]+$/, '').trim())
      .filter(Boolean)
      .join(', ');
  if (!q) return '';
  return `https://maps.google.com/maps?q=${encodeURIComponent(q)}&t=m&z=${zoom}&output=embed&iwloc=near`;
}
